import { STOREMODULE_ENDPOINT_MAP, STOREMODULE_ENDPOINTMODULE_MAP } from '@/assets/js/config/server'
import Vue from 'vue'

import http from '@/$plugins/http'

import base from '../base'
import CHANGE_REQUESTS from '../base/change-requests'
import MAINENTITY from '../base/entity-main'
import NOTES from '../base/entity-notes'
import REQUIRED_ACTIONS from '../base/entity-requiredactions'
import SEARCH from '../base/entity-search-next'
import IS from '../base/is'

export default {
  namespaced: true,
  state: base.createState(IS.state, CHANGE_REQUESTS.state, NOTES.state, REQUIRED_ACTIONS.state, SEARCH.state, MAINENTITY.state, {
    /* define custom state-properties */
  }),
  getters: base.createGetters(IS.getters, CHANGE_REQUESTS.getters, NOTES.getters, REQUIRED_ACTIONS.getters, SEARCH.getters, MAINENTITY.getters, {
    getEntity: state => entityId => {
      // Override to support lookup by both id (GUID) and orderId (string)
      return state.entities.find(e => e.value.id === entityId || e.value.orderId === entityId)
    },
    getEntityUnwrapped: state => entityId => {
      // Override to support lookup by both id (GUID) and orderId (string)
      const entity = state.entities.find(e => e.value.id === entityId || e.value.orderId === entityId)
      return entity ? entity.value : undefined
    }
  }),
  mutations: base.createMutations(IS.mutations, CHANGE_REQUESTS.mutations, NOTES.mutations, REQUIRED_ACTIONS.mutations, SEARCH.mutations, MAINENTITY.mutations, {
    setBasketItem (state, { key, orderId, basketItem }) {
      const index = state.entities.findIndex(item => {
        return (item.value.id === orderId || item.value.orderId === orderId)
      })
      if (index === -1) {
        return
      }

      const order = state.entities[index].value
      const orderItem = { ...state.entities[index] }

      // Update items in the appropriate location (FullOrderModel structure)
      const updateItems = (items) => {
        if (!items || !Array.isArray(items)) {
          return items
        }
        return items.map(x => {
          if (x.basketItemId === basketItem.basketItemId || x.orderItemId === basketItem.basketItemId) {
            return {
              ...x,
              quantity: basketItem.quantity !== undefined ? basketItem.quantity : x.quantity,
              basketItemState: basketItem.itemState || basketItem.basketItemState || x.basketItemState
            }
          }
          return x
        })
      }

      // Update items in various possible locations
      if (order.items && Array.isArray(order.items)) {
        orderItem.value = {
          ...order,
          items: updateItems(order.items)
        }
      } else if (order.orderedBasket && order.orderedBasket.items) {
        orderItem.value = {
          ...order,
          orderedBasket: {
            ...order.orderedBasket,
            items: updateItems(order.orderedBasket.items)
          }
        }
      } else if (order.executedBasket && order.executedBasket.items) {
        orderItem.value = {
          ...order,
          executedBasket: {
            ...order.executedBasket,
            items: updateItems(order.executedBasket.items)
          }
        }
      } else {
        // Fallback to original structure if none match
        orderItem.value = order
      }

      Vue.set(state.entities, index, orderItem)
    }
  }),
  actions: base.createEntityActions(STOREMODULE_ENDPOINTMODULE_MAP.orders, STOREMODULE_ENDPOINT_MAP.orders, IS.actions, CHANGE_REQUESTS.actions, NOTES.actions, REQUIRED_ACTIONS.actions, SEARCH.actions, MAINENTITY.actions, {
    getEntity (serverModuleName = '', serverEntityName = '', entityKey = '') {
      const isKey = `${serverEntityName}/getEntity`

      return function ({ state, getters, commit, dispatch }, orderId = null) {
        // Note: getDefinition is not called here because we're using FullOrderModel
        // which doesn't require the OrderModel entity definition
        commit('setLoading', { key: isKey, loading: true, initial: state.entities.length === 0 })

        return new Promise(resolve => {
          http({
            method: 'get',
            url: `/order/api/order/${orderId}`
          })
            .then(response => {
              // The new endpoint returns FullOrderModel directly in result, not wrapped in result.value
              const entity = response.data.result
              commit('setEntity', { key: entityKey, entity: entity })
              // Use orderId for lookup - getEntityUnwrapped now supports lookup by orderId
              resolve(getters.getEntityUnwrapped(orderId))
            })
            .finally(() => {
              commit('setLoading', { key: isKey, loading: false })
            })
        })
      }
    },
    triggerSetTransmissionState (serverModuleName = '', serverEntityName = '', entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = {}) {
        commit('setSending', { key: 'triggerSetTransmissionState', sending: true })

        return new Promise(resolve => {
          http({
            method: 'put',
            url: `/${serverModuleName}/api/${serverEntityName}/${options.entityId}/transmission/${options.transmissionType}/set/${options.transmissionState}?treatAsOffer=${options.treatAsOffer !== undefined && options.treatAsOffer}`
          })
            .then(response => {
              // SingleResultModel<FullOrderModel> has result as FullOrderModel directly, not wrapped in value
              const fullOrder = response.data.result
              if (fullOrder) {
                commit('setEntity', { key: entityKey, entity: fullOrder })
                resolve(getters.getEntity(fullOrder.id || fullOrder.orderId))
              } else {
                resolve(null)
              }
            })
            .catch(error => {
              console.error('Error setting transmission state:', error)
              resolve(null)
            })
            .finally(() => {
              commit('setSending', { key: 'triggerSetTransmissionState', sending: false })
            })
        })
      }
    },
    updateOrderItem (entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = {}) {
        return new Promise((resolve, reject) => {
          http({
            method: 'put',
            url: `/order/api/orderitems/updateByOrderIdAndBasketItemId/${options.orderId}/${options.basketItemId}`,
            data: options.data
          })
            .then(response => {
              // Update local state with the response
              if (response.data && response.data.result && response.data.result.value) {
                const basketItem = response.data.result.value
                commit('setBasketItem', {
                  key: entityKey,
                  orderId: options.orderId,
                  basketItem: {
                    basketItemId: basketItem.basketItemId || options.basketItemId,
                    quantity: basketItem.quantity,
                    itemState: basketItem.itemState || basketItem.ItemState,
                    basketItemState: basketItem.basketItemState || basketItem.BasketItemState
                  }
                })
              }

              // Refresh the order to get the latest state from server
              return dispatch('getEntity', options.orderId)
                .then(updatedOrder => {
                  // getEntity returns unwrapped order, so return it directly
                  resolve(updatedOrder || getters.getEntityUnwrapped(options.orderId))
                })
                .catch(() => {
                  // If refresh fails, still resolve with current state (unwrapped)
                  resolve(getters.getEntityUnwrapped(options.orderId))
                })
            })
            .catch(error => {
              console.error('Error updating order item:', error)
              reject(error)
            })
        })
      }
    },
    updateOrderConfiguration (entityKey = '') {
        return function ({ state, getters, commit, dispatch }, options = {}) {
            return new Promise(resolve => {
                http({
                    method: 'put',
                    url: `/order/api/order/${options.orderId}/configuration`,
                    data: options.data
                })
                    .then(response => {
                        commit('setEntity', { key: entityKey, entity: response.data.result.value })
                        resolve(getters.getEntity(options.orderId))
                    })
            })
        }
    },
    addOrderItem (entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = {}) {
        return new Promise((resolve, reject) => {
          http({
            method: 'post',
            url: `/order/api/orderitems/addByOrderId/${options.orderId}`,
            data: options.data
          })
            .then(response => {
              // Refresh the order to get the latest state from server
              return dispatch('getEntity', options.orderId)
                .then(updatedOrder => {
                  // getEntity returns unwrapped order, so return it directly
                  resolve(updatedOrder || getters.getEntityUnwrapped(options.orderId))
                })
                .catch(() => {
                  // If refresh fails, still resolve with current state (unwrapped)
                  resolve(getters.getEntityUnwrapped(options.orderId))
                })
            })
            .catch(error => {
              console.error('Error adding order item:', error)
              reject(error)
            })
        })
      }
    },
    deleteOrderItem (entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = {}) {
        return new Promise((resolve, reject) => {
          http({
            method: 'delete',
            url: `/order/api/orderitems/deleteByOrderIdAndBasketItemId/${options.orderId}/${options.basketItemId}`
          })
            .then(response => {
              // Refresh the order to get the latest state from server
              return dispatch('getEntity', options.orderId)
                .then(updatedOrder => {
                  // getEntity returns unwrapped order, so return it directly
                  resolve(updatedOrder || getters.getEntityUnwrapped(options.orderId))
                })
                .catch(() => {
                  // If refresh fails, still resolve with current state (unwrapped)
                  resolve(getters.getEntityUnwrapped(options.orderId))
                })
            })
            .catch(error => {
              console.error('Error deleting order item:', error)
              reject(error)
            })
        })
      }
    },
    replaceOrderItem (entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = {}) {
        return new Promise((resolve, reject) => {
          http({
            method: 'put',
            url: `/order/api/orderitems/replaceByOrderIdAndBasketItemId/${options.orderId}/${options.basketItemId}`,
            data: options.data
          })
            .then(response => {
              // Refresh the order to get the latest state from server
              return dispatch('getEntity', options.orderId)
                .then(updatedOrder => {
                  // getEntity returns unwrapped order, so return it directly
                  resolve(updatedOrder || getters.getEntityUnwrapped(options.orderId))
                })
                .catch(() => {
                  // If refresh fails, still resolve with current state (unwrapped)
                  resolve(getters.getEntityUnwrapped(options.orderId))
                })
            })
            .catch(error => {
              console.error('Error replacing order item:', error)
              reject(error)
            })
        })
      }
    },
    undeleteOrderItem (entityKey = '') {
      return function ({ state, getters, commit, dispatch }, options = {}) {
        return new Promise((resolve, reject) => {
          http({
            method: 'put',
            url: `/order/api/orderitems/undeleteByOrderIdAndBasketItemId/${options.orderId}/${options.basketItemId}`
          })
            .then(response => {
              // Refresh the order to get the latest state from server
              return dispatch('getEntity', options.orderId)
                .then(updatedOrder => {
                  // getEntity returns unwrapped order, so return it directly
                  resolve(updatedOrder || getters.getEntityUnwrapped(options.orderId))
                })
                .catch(() => {
                  // If refresh fails, still resolve with current state (unwrapped)
                  resolve(getters.getEntityUnwrapped(options.orderId))
                })
            })
            .catch(error => {
              console.error('Error undeleting order item:', error)
              reject(error)
            })
        })
      }
    },
  })
}
